// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import Checkbox from "@components/Checkbox/Checkbox";
import DefaultFields from "../../components/DefaultFields/DefaultFields";
import EditableField from "../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";


export default class CobCustomerInformation extends React.PureComponent {
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired,
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	render() {
		const {props, state} = this;
		const {title, session} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		let ssnShort = state.fields.customer_social_security_number.replaceAll("-", "");
		if (ssnShort.length > 10) {
			ssnShort = ssnShort.substring(2, ssnShort.length);
		}

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<input
					type="hidden"
					name="checkbox_company"
					value={session.isCompany ? "yes" : "no"}
				/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					{!session.lookupData && (
						<FieldsCollection
							fields={[
								{
									render: (
										<Checkbox
											label="Är ett bolag"
											checked={session.isCompany}
											onChange={this._onIsCompanyChange}
										/>
									),
								},
							]}
						/>
					)}

					<FieldsCollection
						fieldsPerRow={session.isCompany ? 2 : 3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_firstname"
										label={session.isCompany ? "Bolag" : "Förnamn"}
										value={state.fields.customer_firstname}
										valid={!!session.lookupData || state.validFields.customer_firstname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							}, {
								render: (
									<EditableField
										name="customer_lastname"
										label="Efternamn"
										value={state.fields.customer_lastname}
										valid={!!session.lookupData || state.validFields.customer_lastname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
								display: !session.isCompany,
							}, {
								render: (
									<EditableField
										name="customer_social_security_number"
										label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
										placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
										value={state.fields.customer_social_security_number}
										readOnly={true}
										valid={true}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							}
						]}
					/>

					<input type="hidden" name="customer_name"
						   value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)}/>

					<input type="hidden" name="customer_autogiro_custno"
						   value={state.fields.customer_social_security_number}/>

					<input type="hidden" name="customer_social_security_number"
						   value={ssnShort}/>

					<FieldsCollection
						fieldsPerRow={3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_address_street"
										label="Utdelningsadress"
										value={state.fields.customer_address_street}
										valid={!!session.lookupData || state.validFields.customer_address_street}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							}, {
								render: (
									<EditableField
										name="customer_address_zipcode"
										label="Postnummer"
										value={state.fields.customer_address_zipcode}
										valid={!!session.lookupData || state.validFields.customer_address_zipcode}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							}, {
								render: (
									<EditableField
										name="customer_address_city"
										label="Postort"
										value={state.fields.customer_address_city}
										valid={!!session.lookupData || state.validFields.customer_address_city}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							},
						]}
					/>

					<FieldsCollection
						fieldsPerRow={session.isCompany ? 3 : 2}
						fields={[
							{
								render: (
									<EditableField
										type="tel"
										name="customer_phonenumber_cell"
										label="Telefon"
										value={state.fields.customer_phonenumber_cell}
										valid={state.validFields.customer_phonenumber_cell}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
									/>
								),
							}, {
								render: (
									<EditableField
										type="email"
										name="customer_email"
										label="E-postadress"
										value={state.fields.customer_email}
										valid={state.validFields.customer_email}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								),
							}, {
								display: session.isCompany,
								render: (
									<div style={{
										width: "100%",
										alignItems: "flex-end"
									}}>
										<EditableField
											name="customer_lei"
											label="LEI-kod (20 tecken)"
											value={state.fields.customer_lei}
											valid={state.validFields.customer_lei}
											onChange={this._onFieldChange}
											onBlur={this._onFieldBlur}
										/>
										<span style={{
											display: "block",
											fontSize: "11px",
											fontWeight: "normal",
											padding: "5px 5px 0 0"
										}}>
                                            <a href="https://www.gleif.org/en/lei/search"
											   target="_blank" rel="noopener noreferrer">Sök LEI-kod</a>
                                        </span>
									</div>
								),
							},
						]}
					/>
				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name === "customer_social_security_number") {
			return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
		}
		else if (name === "customer_email") {
			return Validator.validateEmail(value);
		}
		else if (name === "customer_lei") {
			return value.length === 20;
		}
		else {
			return value.length > 0;
		}
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onIsCompanyChange = (checked) => {
		const {session, onChange} = this.props;

		session.isCompany = checked;
		if (this.props.session.isCompany !== checked) this.setState(_getInitialState.call(this), () => {
			onChange({...this.state}, false, this.props.number);
		});
	}
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		"customer_firstname",
		"customer_lastname",
		"customer_social_security_number",
		"customer_address_street",
		"customer_address_zipcode",
		"customer_address_city",
		"customer_phonenumber_cell",
		"customer_email",
		"customer_lei",
	];
}

function _getInitialState() {
	if (this.props.prevState) return {...this.props.prevState};

	const {session} = this.props;
	const {isCompany} = session;
	const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

	return {
		validFields: !session.lookupData ? {} : {
			"customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
			"customer_address_street": !!getLookupData("street"),
			"customer_address_zipcode": !!getLookupData("zipCode"),
			"customer_address_city": !!getLookupData("city"),
			...(
				!isCompany ? {
					"customer_lastname": !!getLookupData("lastName")
				} : undefined
			),
		},
		fields: {
			"customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_lastname": !isCompany ? getLookupData("lastName") : "",
			"customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
			"customer_address_street": getLookupData("street"),
			"customer_address_zipcode": getLookupData("zipCode"),
			"customer_address_city": getLookupData("city"),
			"customer_phonenumber_cell": getLookupData("phoneNumber"),
			"customer_email": "",
			"customer_lei": "",
		},
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/CustomerInformation.js